import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { STATUS_HEX, fmtNum, fmtPct } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

const inr = (n) => `₹${fmtNum(Math.round(n || 0))}`;

const TT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 text-xs shadow-xl">
      <div className="font-semibold mb-1">{label}</div>
      <div className="font-mono space-y-0.5 text-slate-700">
        <div>MVA earned {inr(d.mva_earned)}</div>
        <div>MVA target {inr(d.mva_target)}</div>
        {d.daily_target != null && <div>Daily target {inr(d.daily_target)} · Gap {inr(d.gap)}</div>}
        <div>Output {fmtNum(d.actual)} / {fmtNum(d.target)} · {fmtPct(d.achievement_pct)}</div>
      </div>
    </div>
  );
};

export function MvaSectionChart({ data }) {
  return (
    <div className="h-64 w-full min-h-[256px]" data-testid="mva-section-chart">
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 240 }} minHeight={200}>
        <ComposedChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 12, fontFamily: "IBM Plex Mono" }} />
          <YAxis tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
          <Tooltip content={<TT />} cursor={{ fill: "rgba(100,116,139,0.08)" }} />
          <Legend wrapperStyle={{ fontSize: 12, color: "#64748B" }} />
          <Bar dataKey="mva_earned" name="MVA earned (₹)" radius={[4, 4, 0, 0]} maxBarSize={64}>
            {data.map((d, i) => <Cell key={i} fill={STATUS_HEX[d.status] || "#3B82F6"} />)}
          </Bar>
          <Line type="monotone" dataKey="mva_target" name="MVA target (₹)" stroke="#0F172A" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 4, fill: "#0F172A" }} />
          <Line type="monotone" dataKey="daily_target" name="Daily target (₹)" stroke="#7C3AED" strokeWidth={2} dot={{ r: 4, fill: "#7C3AED" }} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MvaProjectTable({ data }) {
  const max = Math.max(...data.map((d) => Math.max(d.mva_target, d.mva_earned)), 1);
  return (
    <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1" data-testid="mva-project-table">
      {!data.length && <div className="text-sm text-slate-500">No project output yet for the current filters</div>}
      {data.map((p) => (
        <div key={p.name} data-testid={`mva-project-${p.name.replace(/\s+/g, "-")}`}>
          <div className="flex items-center justify-between text-xs mb-1 gap-2">
            <span className="font-semibold text-slate-800 truncate">{p.name}</span>
            <span className="font-mono text-slate-600 whitespace-nowrap">{inr(p.mva_earned)} <span className="text-slate-400">/ {inr(p.mva_target)}</span></span>
            <StatusBadge status={p.status} label={fmtPct(p.achievement_pct)} />
          </div>
          <div className="relative h-2 rounded bg-slate-100">
            <div className="h-2 rounded" style={{ width: `${(p.mva_earned / max) * 100}%`, background: STATUS_HEX[p.status] || "#3B82F6" }} />
            <div className="absolute top-[-3px] h-3.5 w-0.5 bg-slate-900" style={{ left: `${(p.mva_target / max) * 100}%` }} title={`Target ${inr(p.mva_target)}`} />
          </div>
        </div>
      ))}
    </div>
  );
}
